import {
  Briefcase, TrendingUp, DollarSign, Trophy, Clock, AlertTriangle,
  Users, ShieldAlert, AlertCircle, CalendarClock, Snowflake, Flame,
} from "lucide-react";
import { Link } from "react-router-dom";
import { registerWidget } from "@/modules/shared/dashboard/registry";
import { KpiWidget } from "@/modules/shared/dashboard/widgets/shells/KpiWidget";
import { ListWidget } from "@/modules/shared/dashboard/widgets/shells/ListWidget";
import { MrrTrendChart } from "./shells/MrrTrendChart";
import { SalesFunnelChart } from "./shells/SalesFunnelChart";
import { fmtBRL, fmtPct, fmtDays } from "@/modules/shared/dashboard/formatters";

// ── KPI registrations ────────────────────────────────────────────────────────

registerWidget({
  id: "sales.activeDeals", module: "sales", title: "Negócios em aberto",
  description: "Total de negócios ativos no pipeline.", size: "1x1", category: "kpi", icon: Briefcase,
  Component: () => (
    <KpiWidget tone="blue" title="Negócios em aberto" endpoint="/sales/dashboard/active-deals"
               icon={Briefcase} href="/clientes" />
  ),
});

registerWidget({
  id: "sales.pipelineValue", module: "sales", title: "Valor em pipeline",
  description: "Soma do MRR dos negócios ativos no pipeline.", size: "1x1", category: "kpi", icon: DollarSign,
  Component: () => (
    <KpiWidget tone="green" title="Valor em pipeline" endpoint="/sales/dashboard/pipeline-value"
               icon={DollarSign} href="/clientes" valueFormatter={fmtBRL} />
  ),
});

registerWidget({
  id: "sales.dealsWonMonth", module: "sales", title: "Ganhos no mês",
  description: "Negócios fechados como ganhos no mês corrente.", size: "1x1", category: "kpi", icon: Trophy,
  Component: () => (
    <KpiWidget tone="green" title="Ganhos no mês" endpoint="/sales/dashboard/deals-won-month"
               icon={Trophy} href="/clientes" />
  ),
});

registerWidget({
  id: "sales.winRate", module: "sales", title: "Taxa de conversão (90d)",
  description: "Percentual de negócios ganhos sobre fechados nos últimos 90 dias.", size: "1x1", category: "kpi", icon: TrendingUp,
  Component: () => (
    <KpiWidget tone="amber" title="Taxa de conversão (90d)" endpoint="/sales/dashboard/win-rate"
               icon={TrendingUp} href="/clientes" valueFormatter={fmtPct} />
  ),
});

registerWidget({
  id: "sales.avgDealSize", module: "sales", title: "Ticket médio",
  description: "Média do MRR dos negócios ganhos nos últimos 90 dias.", size: "1x1", category: "kpi", icon: DollarSign,
  Component: () => (
    <KpiWidget tone="ink" title="Ticket médio" endpoint="/sales/dashboard/avg-deal-size"
               icon={DollarSign} href="/clientes" valueFormatter={fmtBRL} />
  ),
});

registerWidget({
  id: "sales.salesCycleDays", module: "sales", title: "Ciclo de venda (dias)",
  description: "Tempo médio em dias para fechar um negócio (últimos 90 dias).", size: "1x1", category: "kpi", icon: Clock,
  Component: () => (
    <KpiWidget tone="amber" title="Ciclo de venda (dias)" endpoint="/sales/dashboard/sales-cycle-days"
               icon={Clock} href="/clientes" valueFormatter={fmtDays} />
  ),
});

registerWidget({
  id: "sales.activeClients", module: "sales", title: "Clientes ativos",
  description: "Total de clientes com status ativo.", size: "1x1", category: "kpi", icon: Users,
  Component: () => (
    <KpiWidget tone="ink" title="Clientes ativos" endpoint="/sales/dashboard/active-clients"
               icon={Users} href="/clientes" />
  ),
});

registerWidget({
  id: "sales.atRiskClients", module: "sales", title: "Clientes em risco",
  description: "Clientes com alerta de risco ativado manualmente.", size: "1x1", category: "kpi", icon: ShieldAlert,
  Component: () => (
    <KpiWidget tone="red" title="Clientes em risco" endpoint="/sales/dashboard/at-risk-clients"
               icon={ShieldAlert} href="/clientes" />
  ),
});

// ── List registrations ───────────────────────────────────────────────────────

type StaleDay = { id: string; title: string; client_name: string | null; client_id: string | null; days_idle: number };
registerWidget({
  id: "sales.staleDeals", module: "sales", title: "Negócios parados +7d",
  description: "Negócios sem atividade há mais de 7 dias.", size: "2x1", category: "list", icon: AlertCircle,
  Component: () => (
    <ListWidget<StaleDay>
      title="Negócios parados +7d"
      endpoint="/sales/dashboard/stale-deals"
      icon={AlertCircle}
      tone="amber"
      seeAllHref="/clientes"
      renderItem={(d) => {
        const href = d.client_id ? `/clientes/${d.client_id}` : "/clientes";
        return (
          <Link to={href} className="block hover:text-[var(--ink)]">
            <span className="flex items-center justify-between text-sm">
              <span>{d.title} {d.client_name && <span className="text-muted-foreground">· {d.client_name}</span>}</span>
              <span className="text-warning">{d.days_idle}d</span>
            </span>
          </Link>
        );
      }}
    />
  ),
});

type ActionDeal = { id: string; title: string; client_name: string | null; client_id: string | null; next_action_date: string; next_action: string | null };
registerWidget({
  id: "sales.dealsActionToday", module: "sales", title: "Negócios com ação próxima",
  description: "Negócios com ação agendada para os próximos 14 dias.", size: "2x1", category: "list", icon: CalendarClock,
  Component: () => (
    <ListWidget<ActionDeal>
      title="Próximas ações (14d)"
      endpoint="/sales/dashboard/deals-action-today"
      icon={CalendarClock}
      tone="blue"
      seeAllHref="/clientes"
      renderItem={(d) => {
        const href = d.client_id ? `/clientes/${d.client_id}` : "/clientes";
        return (
          <Link to={href} className="block hover:text-[var(--ink)]">
            <span className="flex items-center justify-between text-sm">
              <span>{d.title} {d.client_name && <span className="text-muted-foreground">· {d.client_name}</span>}</span>
              <span className="text-muted-foreground">{d.next_action_date}</span>
            </span>
          </Link>
        );
      }}
    />
  ),
});

type ColdClient = { id: string; name: string; days_cold: number };
registerWidget({
  id: "sales.coldClients", module: "sales", title: "Clientes frios",
  description: "Clientes sem check-in realizado no mês corrente.", size: "2x1", category: "list", icon: Snowflake,
  Component: () => (
    <ListWidget<ColdClient>
      title="Clientes frios"
      endpoint="/sales/dashboard/cold-clients"
      icon={Snowflake}
      tone="red"
      seeAllHref="/clientes"
      renderItem={(c) => (
        <Link to={`/clientes/${c.id}`} className="block hover:text-[var(--ink)]">
          <span className="flex items-center justify-between text-sm">
            <span>{c.name}</span>
            <span className="text-danger">{c.days_cold}d</span>
          </span>
        </Link>
      )}
    />
  ),
});

type HotDeal = { id: string; title: string; client_name: string | null; client_id: string | null; mrr_value: number | null };
registerWidget({
  id: "sales.hotDeals", module: "sales", title: "Negócios quentes",
  description: "Negócios com temperatura quente e maior probabilidade de fechamento.", size: "2x1", category: "list", icon: Flame,
  Component: () => (
    <ListWidget<HotDeal>
      title="Negócios quentes"
      endpoint="/sales/dashboard/hot-deals"
      icon={Flame}
      tone="red"
      seeAllHref="/clientes"
      renderItem={(d) => {
        const href = d.client_id ? `/clientes/${d.client_id}` : "/clientes";
        return (
          <Link to={href} className="block hover:text-[var(--ink)]">
            <span className="flex items-center justify-between text-sm">
              <span>{d.title} {d.client_name && <span className="text-muted-foreground">· {d.client_name}</span>}</span>
              {d.mrr_value != null && (
                <span className="text-success font-medium">
                  {fmtBRL(d.mrr_value)}
                </span>
              )}
            </span>
          </Link>
        );
      }}
    />
  ),
});

// ── Cross-module KPI registrations ───────────────────────────────────────────

registerWidget({
  id: "sales.headcountForPipeline",
  module: "sales",
  title: "Headcount p/ pipeline",
  description: "Diferença entre headcount necessário do pipeline ativo e bench atual.",
  size: "1x1",
  category: "kpi",
  icon: Users,
  Component: () => (
    <KpiWidget
      tone="amber"
      title="Headcount p/ pipeline"
      endpoint="/sales/dashboard/headcount-for-pipeline"
      icon={Users}
      href="/profissionais"
    />
  ),
});

// ── Chart registrations ──────────────────────────────────────────────────────

registerWidget({
  id: "sales.mrrTrend6m", module: "sales", title: "MRR 6 meses",
  description: "Evolução do MRR acumulado dos últimos 6 meses.",
  size: "2x1", category: "chart", icon: TrendingUp,
  Component: MrrTrendChart,
});

registerWidget({
  id: "sales.dealsFunnel", module: "sales", title: "Funil de negócios",
  description: "Distribuição de negócios em aberto por etapa do funil.",
  size: "2x1", category: "chart", icon: AlertTriangle,
  Component: SalesFunnelChart,
});
